import React, { useEffect, useState } from "react";
import { Menu } from "@headlessui/react";
import { HiOutlinePlus } from "react-icons/hi";
import { toast } from "react-hot-toast";
import LinksComponent from "../../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../../components/Tenant/DataTableComponent/DataTableComponent";
import ModalComponent from "../../../../components/Tenant/ModalComponent/ModalComponent";
import { CircleLoaderComponent } from "../../../../components/Loader/CircleLoaderComponent";
import {
  useAddDistrictMasterMutation,
  useDeleteDistrictMasterMutation,
  useFetchDistrictMasterListMutation,
  useFetchDistrictMasterStatesMutation,
  useUpdateDistrictMasterMutation,
  useUploadDistrictMasterCsvMutation,
} from "../../../../features/tenant/masters/zoneStateMasterApi";
import { downloadSampleCSV } from "../../../../utils/downloadSampleCsv";
import { parseCSV } from "../../../../utils/parseCsv";
import { downloadJsonAsCsv } from "../../../../utils/downloadJsonAsCsv";

const DISTRICT_NAME_MAX_LENGTH = 255;
const initialFilters = { district: "", state_id: "" };
const initialForm = { id: null, district: "", state_id: "", original_state_id: "", location_count: 0 };
const districtCsvTemplate = [
  { name: "state", label: "Sample State", maxLength: 255 },
  { name: "district", label: "Sample District", maxLength: DISTRICT_NAME_MAX_LENGTH },
];

const inputClass =
  "mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500";

const DistrictMasterList = ({ embedded = false }) => {
  const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme")).theme.color_shades;
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState(initialFilters);
  const [districtList, setDistrictList] = useState([]);
  const [total, setTotal] = useState(0);
  const [states, setStates] = useState([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [addTab, setAddTab] = useState("manual");
  const [csvFile, setCsvFile] = useState(null);

  const [fetchStates, { isLoading: statesLoading }] = useFetchDistrictMasterStatesMutation();
  const [fetchDistrictList, { isLoading: listLoading }] = useFetchDistrictMasterListMutation();
  const [addDistrict, { isLoading: addLoading }] = useAddDistrictMasterMutation();
  const [updateDistrict, { isLoading: updateLoading }] = useUpdateDistrictMasterMutation();
  const [deleteDistrict, { isLoading: deleteLoading }] = useDeleteDistrictMasterMutation();
  const [uploadDistrictCsv, { isLoading: csvUploading }] = useUploadDistrictMasterCsvMutation();
  const [fetchDistrictListForExport, { isLoading: exportLoading }] = useFetchDistrictMasterListMutation();

  const isEdit = Boolean(form.id);
  const isSaving = addLoading || updateLoading;
  // A mapped district cannot move to another state (the backend rejects it too). Old districts without a state
  // stay editable so a state can be assigned; the backend checks it matches their zone locations.
  const stateLocked = isEdit && Number(form.location_count) > 0 && Boolean(form.original_state_id);

  const authParams = () => ({
    token: sessionStorage.getItem("access_token"),
    tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
  });

  const loadDistricts = async (appliedFilters = filters, pageLimit = limit, pageOffset = offset) => {
    try {
      const res = await fetchDistrictList({
        ...authParams(),
        body: { ...appliedFilters, limit: pageLimit, offset: pageOffset },
      }).unwrap();
      setDistrictList(res?.body?.rows || []);
      setTotal(res?.body?.total || 0);
    } catch (error) {
      toast.error(error?.data?.message || "Error fetching district list");
    }
  };

  useEffect(() => {
    fetchStates(authParams())
      .unwrap()
      .then((res) => setStates(Array.isArray(res?.body) ? res.body : []))
      .catch((error) => toast.error(error?.data?.message || "Error fetching states"));
  }, []);

  useEffect(() => {
    loadDistricts();
  }, [limit, offset]);

  const handlePageChange = (pageNumber) => {
    setOffset((pageNumber - 1) * limit);
    setCurrentPage(pageNumber);
  };

  const handlePerRowsChange = (newPerPage) => {
    setLimit(newPerPage);
    setOffset(0);
    setCurrentPage(1);
  };

  const applyFilters = (appliedFilters) => {
    setCurrentPage(1);
    if (offset === 0) {
      loadDistricts(appliedFilters, limit, 0);
    } else {
      setOffset(0);
    }
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    applyFilters(initialFilters);
  };

  const openAddModal = () => {
    setForm(initialForm);
    setAddTab("manual");
    setCsvFile(null);
    setShowFormModal(true);
  };

  const openEditModal = (row) => {
    setForm({
      id: row.id,
      district: row.district || "",
      state_id: row.state_id ? String(row.state_id) : "",
      original_state_id: row.state_id ? String(row.state_id) : "",
      location_count: row.location_count || 0,
    });
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setForm(initialForm);
    setAddTab("manual");
    setCsvFile(null);
  };

  const handleCsvSubmit = () => {
    if (!csvFile) {
      toast.error("Please select a file");
      return;
    }

    const fileReader = new FileReader();
    fileReader.onload = async (event) => {
      const rows = parseCSV(event.target.result);
      try {
        const res = await uploadDistrictCsv({ ...authParams(), body: { rows } }).unwrap();
        const insertedCount = res?.body?.inserted?.length || 0;
        const skippedCount = res?.body?.skipped?.length || 0;
        toast.success(`${insertedCount} district(s) added, ${skippedCount} skipped`);
        closeFormModal();
        loadDistricts();
      } catch (error) {
        toast.error(error?.data?.message || "Error uploading district CSV");
      }
    };
    fileReader.readAsText(csvFile);
  };

  const handleExportCsv = async () => {
    try {
      const res = await fetchDistrictListForExport({ ...authParams(), body: { limit: 100000, offset: 0 } }).unwrap();
      const rows = res?.body?.rows || [];
      if (rows.length === 0) {
        toast.error("No districts to export");
        return;
      }
      downloadJsonAsCsv(rows, "district_master.csv");
    } catch (error) {
      toast.error(error?.data?.message || "Error exporting district CSV");
    }
  };

  const handleSave = async () => {
    const districtName = form.district.trim();

    if (!districtName || !form.state_id) {
      toast.error("District name and state are required");
      return;
    }

    const body = { district: districtName, state_id: Number(form.state_id) };

    try {
      if (isEdit) {
        await updateDistrict({ ...authParams(), body: { id: form.id, ...body } }).unwrap();
        toast.success("District updated successfully");
      } else {
        await addDistrict({ ...authParams(), body }).unwrap();
        toast.success("District added successfully");
      }
      closeFormModal();
      loadDistricts();
    } catch (error) {
      toast.error(error?.data?.message || (isEdit ? "Error updating district" : "Error adding district"), {
        duration: 6000,
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteDistrict({ ...authParams(), body: { id: deleteTarget.id } }).unwrap();
      toast.success("District deleted successfully");
      setDeleteTarget(null);

      // If the last row of a later page was deleted, step back one page.
      if (districtList.length === 1 && offset > 0) {
        setOffset(Math.max(0, offset - limit));
        setCurrentPage((page) => Math.max(1, page - 1));
      } else {
        loadDistricts();
      }
    } catch (error) {
      setDeleteTarget(null);
      toast.error(error?.data?.message || "Error deleting district", { duration: 6000 });
    }
  };

  const columns = [
    { name: "SNo", cell: (row, idx) => offset + Number(idx + 1), width: "80px" },
    { name: "District", selector: (row) => row.district || "-" },
    { name: "State", selector: (row) => row.state || "-" },
    { name: "Zone Locations", selector: (row) => row.location_count ?? 0 },
    {
      name: "",
      minWidth: "180px",
      cell: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => openEditModal(row)}
            className="flex justify-center items-center rounded px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow transition duration-150 ease-in-out"
            style={{ backgroundColor: primaryColor?.["700"] || "#2563eb" }}
          >
            Edit
          </button>
          <button
            onClick={() => setDeleteTarget(row)}
            className="flex justify-center items-center rounded bg-red-600 px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow transition duration-150 ease-in-out hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const content = (
    <div className="p-3">
      <div className="p-3 w-full bg-white shadow rounded-lg">
        <div className="border-b border-gray-300 bg-gray-50 rounded-t-lg p-3">
          <div className="flex space-x-2 justify-end">
            <div className="w-52 h-10">
              <div className="relative searchin">
                <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">District</label>
                <input
                  className="w-full px-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                  value={filters.district}
                  onChange={(e) => setFilters({ ...filters, district: e.target.value })}
                />
              </div>
            </div>
            <div className="w-52 h-10">
              <div className="relative searchin">
                <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">State</label>
                <select
                  className="w-full px-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                  value={filters.state_id}
                  onChange={(e) => setFilters({ ...filters, state_id: e.target.value })}
                >
                  <option value="">All</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.state}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-row gap-2 h-10 mt-6">
              <button className="btn btn-outline-primary p-2" onClick={() => applyFilters(filters)}>
                Search
              </button>
              <button className="btn btn-outline-primary p-2" onClick={clearFilters}>
                Clear
              </button>
            </div>
          </div>
        </div>

        <DataTableComponent
          keyField="id"
          columns={columns}
          data={districtList}
          totalRows={total}
          handlePerRowsChange={handlePerRowsChange}
          handlePageChange={handlePageChange}
          paginationDefaultPage={currentPage}
        />

        <div className="fixed bottom-0 right-10 w-16 h-16 mr-10 mb-6 cursor-pointer" id="box_btn">
          <div className="text-right">
            <Menu className="relative inline-block text-left">
              <div>
                <Menu.Button
                  onClick={openAddModal}
                  className="inline-flex justify-cente w-12 h-12 rounded-full text-sm font-medium text-white hover:bg-opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
                  style={{ backgroundColor: `${primaryColor["700"]}` }}
                >
                  <HiOutlinePlus className="w-full h-full p-3" />
                </Menu.Button>
              </div>
            </Menu>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {(listLoading || statesLoading || isSaving || deleteLoading || exportLoading) && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/60 backdrop-blur-sm">
          <CircleLoaderComponent />
        </div>
      )}

      {embedded ? (
        content
      ) : (
        <div className="flex w-full bg-gray-200 h-full justify-center">
          <div className="w-full">
            <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
              District List
              <LinksComponent />
            </div>
            {content}
          </div>
        </div>
      )}

      {showFormModal && (
        <ModalComponent show={showFormModal} setShow={closeFormModal} size="md">
          <div className="p-6 flex flex-col gap-4">
            <h2 className="text-lg font-semibold text-gray-800 border-b pb-2">
              {isEdit ? "Edit District" : "Add District"}
            </h2>

            {!isEdit && (
              <div className="flex justify-center gap-2 border-b">
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    addTab === "manual"
                      ? "border-blue-600 text-blue-700"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setAddTab("manual")}
                >
                  Add Manually
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    addTab === "csv"
                      ? "border-blue-600 text-blue-700"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setAddTab("csv")}
                >
                  Upload CSV
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    addTab === "export"
                      ? "border-blue-600 text-blue-700"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setAddTab("export")}
                >
                  Export CSV
                </button>
              </div>
            )}

            {isEdit || addTab === "manual" ? (
              <>
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">State *</label>
                    <select
                      className={`${inputClass} ${stateLocked ? "bg-gray-100 cursor-not-allowed" : ""}`}
                      value={form.state_id}
                      disabled={stateLocked}
                      onChange={(e) => setForm({ ...form, state_id: e.target.value })}
                    >
                      <option value="">Select state</option>
                      {states.map((state) => (
                        <option key={state.id} value={state.id}>
                          {state.state}
                        </option>
                      ))}
                    </select>
                    {states.length === 0 && !statesLoading && (
                      <p className="mt-1 text-xs text-red-600">No states found. Add states from State List first.</p>
                    )}
                    {stateLocked && (
                      <p className="mt-1 text-xs text-gray-500">
                        State cannot be changed because this district is mapped to {form.location_count} zone location(s).
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">District Name *</label>
                    <input
                      type="text"
                      maxLength={DISTRICT_NAME_MAX_LENGTH}
                      value={form.district}
                      onChange={(e) => setForm({ ...form, district: e.target.value })}
                      placeholder="e.g. Lucknow"
                      className={inputClass}
                    />
                  </div>

                  {isEdit && Number(form.location_count) > 0 && (
                    <p className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-300 rounded-md p-2">
                      A new name applies to all {form.location_count} zone location(s) using this district.
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-3 border-t mt-2">
                  <button onClick={closeFormModal} className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-100">
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !form.district.trim() || !form.state_id}
                    className={`px-4 py-2 text-sm text-white rounded ${
                      isSaving || !form.district.trim() || !form.state_id
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {isEdit ? "Update" : "Add"}
                  </button>
                </div>
              </>
            ) : addTab === "csv" ? (
              <div className="flex flex-col gap-3">
                <p className="text-xs text-gray-500">
                  CSV columns: <span className="font-semibold">state</span> and{" "}
                  <span className="font-semibold">district</span>. The state must already exist; matching districts
                  already in that state are skipped.
                </p>

                <div className="flex justify-center">
                  <button
                    type="button"
                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                    onClick={() => downloadSampleCSV(districtCsvTemplate)}
                  >
                    Sample CSV Download
                  </button>
                </div>

                <label
                  htmlFor="district-csv-dropzone"
                  className="flex flex-col items-center justify-center w-full h-20 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                >
                  <p className="text-sm text-gray-500 text-center">
                    <span className="font-semibold">Choose CSV file</span>
                    <div>Click to upload or drag and drop</div>
                  </p>
                  <input
                    id="district-csv-dropzone"
                    type="file"
                    className="hidden"
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                  />
                </label>
                {csvFile && <p className="text-xs text-gray-500 text-center">{csvFile.name}</p>}

                <div className="flex justify-end gap-3 pt-3 border-t mt-2">
                  <button onClick={closeFormModal} className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-100">
                    Cancel
                  </button>
                  <button
                    onClick={handleCsvSubmit}
                    disabled={csvUploading || !csvFile}
                    className={`px-4 py-2 text-sm text-white rounded ${
                      csvUploading || !csvFile ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {csvUploading ? "Uploading..." : "Upload"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-gray-600">Download every district currently in the system as a CSV file.</p>
                <div className="flex justify-end gap-3 pt-3 border-t mt-2">
                  <button onClick={closeFormModal} className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-100">
                    Cancel
                  </button>
                  <button
                    onClick={handleExportCsv}
                    disabled={exportLoading}
                    className={`px-4 py-2 text-sm text-white rounded ${
                      exportLoading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {exportLoading ? "Exporting..." : "Download CSV"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </ModalComponent>
      )}

      {deleteTarget && (
        <ModalComponent show={Boolean(deleteTarget)} setShow={() => setDeleteTarget(null)} size="md">
          <div className="p-6 flex flex-col gap-4">
            <h2 className="text-lg font-semibold text-gray-800 border-b pb-2">Delete District</h2>
            <p className="text-sm text-gray-700">
              Are you sure you want to delete district <span className="font-semibold">{deleteTarget.district}</span>
              {deleteTarget.state ? ` (${deleteTarget.state})` : ""}? This cannot be undone.
            </p>
            {Number(deleteTarget.location_count) > 0 && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-300 rounded-md p-2">
                This district is mapped to {deleteTarget.location_count} zone location(s) and cannot be deleted.
              </p>
            )}
            <div className="flex justify-end gap-3 pt-3 border-t">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm text-gray-700 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteLoading}
                className="px-4 py-2 text-sm text-white rounded bg-red-600 hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </ModalComponent>
      )}
    </>
  );
};

export default DistrictMasterList;
